import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OnboardingAdjusting from '../../assets/OnboardingAdjusting.svg?react';
import OnboardingAutoposition from '../../assets/OnboardingAutoposition.svg?react';
import OnboardingLuminiares from '../../assets/OnboardingLuminiares.svg?react';
import { useSupportContactEnabled } from '../../hooks/useIdlc1129Features';
import { useSupportMailto } from '../../hooks/useSupportMailto.ts';
import { SUPPORT_EMAIL } from '../../utils/support.ts';

interface CheatSheetProps {
  onClose: () => void;
}

interface ShortcutRow {
  keys: string;
  description: string;
}

// Toggle switches illustration for step 2
const ToggleSwitchesIllustration: React.FC = () => (
  <div className="flex justify-center gap-5 my-4">
    {/* CHL Toggle - Active */}
    <div className="flex flex-col items-center">
      <span className="mb-1 text-[10px] font-bold text-gray-300">CHL</span>
      <svg width="40" height="22" viewBox="0 0 60 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="34" rx="17" fill="#4B5563" />
        <circle cx="43" cy="17" r="13" fill="#0EA5E9" />
      </svg>
    </div>

    {/* Lmn Toggle - Inactive */}
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-1 mb-1 text-[10px] font-bold text-gray-500">
        <span>Lmn</span>
      </div>
      <svg width="40" height="22" viewBox="0 0 60 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="58" height="32" rx="16" stroke="#4B5563" strokeWidth="2" />
        <circle cx="17" cy="17" r="12" stroke="#4B5563" strokeWidth="2" />
      </svg>
    </div>
  </div>
);

export function CheatSheet({ onClose }: CheatSheetProps) {
  const { t } = useTranslation();
  const supportMailto = useSupportMailto();
  const supportContact = useSupportContactEnabled();
  const [activeTab, setActiveTab] = useState<'shortcuts' | 'help'>('help');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const handleKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleKeyDownCapture, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleKeyDownCapture, true);
    };
  }, [onClose]);

  const sections: { title: string; shortcuts: ShortcutRow[] }[] = [
    {
      title: t('workspace.cheatSheet.lampMovement'),
      shortcuts: [
        { keys: '← →', description: t('workspace.cheatSheet.moveLeftRight') },
        { keys: '↑ ↓', description: t('workspace.cheatSheet.moveForwardBack') },
        { keys: 'Shift + ↑', description: t('workspace.cheatSheet.moveUp') },
        { keys: 'Shift + ↓', description: t('workspace.cheatSheet.moveDown') },
      ],
    },
    {
      title: t('workspace.cheatSheet.history'),
      shortcuts: [
        { keys: '⌘/Ctrl + Z', description: t('workspace.cheatSheet.undo') },
        { keys: '⌘/Ctrl + Shift + Z', description: t('workspace.cheatSheet.redo') },
      ],
    },
    {
      title: t('workspace.cheatSheet.layers'),
      shortcuts: [
        { keys: '⌘/Ctrl + Shift + 1', description: t('workspace.cheatSheet.toggleCHL') },
        { keys: '⌘/Ctrl + Shift + 2', description: t('workspace.cheatSheet.toggleLMN') },
        { keys: 'Del / Backspace', description: t('workspace.cheatSheet.deleteSelectedLamp') },
      ],
    },
    {
      title: t('workspace.cheatSheet.measurement'),
      shortcuts: [
        { keys: '⌘/Ctrl + Click', description: t('workspace.cheatSheet.startMeasurement') },
        { keys: 'Click', description: t('workspace.cheatSheet.addMeasurementPoint') },
        { keys: 'Esc', description: t('workspace.cheatSheet.cancelMeasurement') },
      ],
    },
    {
      title: t('workspace.cheatSheet.camera'),
      shortcuts: [
        { keys: t('workspace.cheatSheet.scrollWheel'), description: t('workspace.cheatSheet.zoom') },
        { keys: t('workspace.cheatSheet.rightClickDrag'), description: t('workspace.cheatSheet.rotate') },
        { keys: t('workspace.cheatSheet.middleClickDrag'), description: t('workspace.cheatSheet.pan') },
      ],
    },
  ];

  const onboardingSteps = useMemo(
    () => [
      {
        title: t('onboarding.steps.setup.title'),
        icon: <OnboardingAdjusting className="w-5 h-5 text-white invert" />,
        mainTitle: t('onboarding.steps.setup.mainTitle'),
        description: t('onboarding.steps.setup.description'),
        mainTitleThird: t('onboarding.steps.setup.examplesTitle'),
        descriptionThird: t('onboarding.steps.setup.examplesDescription'),
      },
      {
        title: t('onboarding.steps.layers.title'),
        icon: <OnboardingAdjusting className="w-5 h-5 text-white invert" />,
        mainTitle: t('onboarding.steps.layers.mainTitle'),
        description: t('onboarding.steps.layers.description'),
        mainTitleSecond: t('onboarding.steps.layers.chlTitle'),
        descriptionSecond: t('onboarding.steps.layers.chlDescription'),
        mainTitleThird: t('onboarding.steps.layers.lmnTitle'),
        descriptionThird: t('onboarding.steps.layers.lmnDescription'),
        hasToggleIllustration: true,
      },
      {
        title: t('onboarding.steps.autoposition.title'),
        icon: <OnboardingLuminiares className="w-5 h-5 text-lime-400 invert" />,
        mainTitle: t('onboarding.steps.autoposition.mainTitle'),
        description: t('onboarding.steps.autoposition.description'),
        mainTitleThird: t('onboarding.steps.autoposition.optimizationTitle'),
        descriptionThird: t('onboarding.steps.autoposition.optimizationDescription'),
        hasAutopositionIllustration: true,
      },
    ],
    [t],
  );

  return (
    <>
      <div className="fixed inset-0 z-[200]" onClick={onClose} />

      <div className="fixed left-[90px] top-1/2 -translate-y-1/2 z-[201] bg-[#1E1E1E] border border-white/10 rounded-xl shadow-2xl w-[360px] h-[600px] overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header with Tabs */}
        <div className="px-4 pt-4 pb-2 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <span className="text-base font-semibold text-white">{t('workspace.cheatSheet.title')}</span>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('help')}
              className={`pb-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'help' ? 'text-[#A3E635] border-[#A3E635]' : 'text-gray-400 border-transparent'
              }`}
            >
              {t('workspace.cheatSheet.workspaceHelp')}
            </button>
            <button
              onClick={() => setActiveTab('shortcuts')}
              className={`pb-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'shortcuts' ? 'text-[#A3E635] border-[#A3E635]' : 'text-gray-400 border-transparent'
              }`}
            >
              {t('workspace.cheatSheet.shortcuts')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-4 custom-scrollbar">
          {activeTab === 'shortcuts' ? (
            <div className="space-y-4">
              {sections.map((section) => (
                <div key={section.title}>
                  <div className="text-xs font-bold text-[#A3E635] uppercase tracking-wider mb-2">{section.title}</div>
                  <div className="space-y-1.5">
                    {section.shortcuts.map((shortcut) => (
                      <div key={shortcut.keys} className="flex items-center justify-between gap-3">
                        <kbd className="inline-flex items-center px-2 py-0.5 rounded bg-white/5 border border-white/10 text-xs font-mono text-gray-300 whitespace-nowrap">
                          {shortcut.keys}
                        </kbd>
                        <span className="text-xs text-gray-400 text-right">{shortcut.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-white/10">
                <p className="text-xs text-gray-500 leading-relaxed">{t('workspace.cheatSheet.hint')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {onboardingSteps.map((step, index) => (
                <div key={index} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="shrink-0">{step.icon}</div>
                    <h3 className="text-base font-bold text-white">{step.mainTitle}</h3>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">{step.description}</p>

                  {step.hasToggleIllustration && <ToggleSwitchesIllustration />}

                  {step.mainTitleSecond && (
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-gray-200">{step.mainTitleSecond}</h4>
                      <p className="text-xs text-gray-400 leading-relaxed">{step.descriptionSecond}</p>
                    </div>
                  )}

                  {step.hasAutopositionIllustration && (
                    <div className="flex justify-center opacity-80 scale-75 invert">
                      <OnboardingAutoposition />
                    </div>
                  )}

                  <div className="space-y-1">
                    <h4 className="text-xs font-bold text-gray-200">{step.mainTitleThird}</h4>
                    <p className="text-xs text-gray-400 leading-relaxed">{step.descriptionThird}</p>
                  </div>

                  {index < onboardingSteps.length - 1 && <div className="border-b border-white/5 pt-2" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {supportContact && (
          <div className="px-4 py-3 border-t border-white/10 text-xs text-gray-400">
            {t('support.stuck')}{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              onClick={(e) => {
                e.currentTarget.href = supportMailto();
              }}
              className="text-[#A3E635] hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>
        )}
      </div>
    </>
  );
}
